import { formatUserName } from "@impero-com/common/utils/formatting";
import { TFunction } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";

import {
    ControlResultStepWithoutFields,
    Performance,
} from "api/generated/model";
import { getStepActorName } from "utils/controlResults/getStepActorName";
import { LookupContext } from "utils/controlResults/lookupContext";

/** Who acts on a step. The Impero system has no name until it is localised. */
export type ActorName =
    | { type: "Named"; name: string }
    | { type: "ImperoSystem" };

export function localizeActorName(name: ActorName, t: TFunction): string {
    switch (name.type) {
        case "Named":
            return name.name;
        case "ImperoSystem":
            return t("ControlResult_Performer_ImperoSystem");
        default:
            return assertNever(name);
    }
}

/** Who performed the step, `null` when nobody did or the user is unknown. */
export function getPerformerName(
    performance: Performance | null,
    lookupContext: LookupContext
): ActorName | null {
    if (!performance) {
        return null;
    }
    const { performer } = performance;
    switch (performer.type) {
        case "User": {
            const user = lookupContext.usersById.get(performer.userId);
            return user ? { type: "Named", name: formatUserName(user) } : null;
        }
        case "System":
            return { type: "ImperoSystem" };
        default:
            return assertNever(performer);
    }
}

/** The step's assignee, or the Impero system when it performed the step. */
export function getStepAssigneeOrSystemName(
    step: ControlResultStepWithoutFields,
    lookupContext: LookupContext
): ActorName {
    const performerType = step.performance?.performer.type;
    switch (performerType) {
        case undefined:
        case "User":
            return {
                type: "Named",
                name: getStepActorName(step, lookupContext),
            };
        case "System":
            return { type: "ImperoSystem" };
        default:
            return assertNever(performerType);
    }
}
